import { LoadingOutlined } from "@ant-design/icons";
import { getAuthorizationHeader } from "@api/common-utils";
import { baseUrl, vercelEnv } from "@api/const";
import ProjectsTable from "@components/pages/ExistingProject/components/ProjectsTable/ProjectsTable";
import { ContentToExport } from "@commonComponents/gcf-congratulation/components/ContentToExport";
import { useStore } from "@stores/root-store";
import { handleExportToDoc } from "@utils/common-functions";
import { constRoute } from "@utils/route";
import { Spin } from "antd";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
//@ts-ignore
import { v4 as uuidv4 } from "uuid";
import exportDataIcon from "../../../assets/icons/download-data.svg";
import pencilIcon from "../../../assets/icons/edit-2.svg";
import trashIcon from "../../../assets/icons/trash-2.svg";
import ProjectDeleteModelData from "./projectDeleteModel";
import style from "./style.module.scss";

import { sectionsMap as climateRationaleSectionsMap } from "@components/pages/project/single-functions/climate-rationale-advisor/main-page";
import { sectionsMap as theoryOfChangeSectionsMap } from "@components/pages/project/single-functions/theory-of-change/main-page";
import { sectionsMap as noObjectionProcedureSectionsMap } from "@components/pages/project/single-functions/no-objection-procedure/main-page";
import { sectionsMap as renewableProjectSectionsMap } from "@components/pages/project/single-functions/project-structuring/main-page";
import { sectionsMap as evaluateSectionsMap } from "@components/pages/project/single-functions/evaluate-a-cn/main-page/index";
import { sectionsMap as draftSectionsMap } from "@components/pages/project/single-functions/refine-a-cn/main-page/index";

const ExistingProject = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const email: string = localStorage.getItem("email");
  const { pathname } = useLocation();
  //const isTestEnv = vercelEnv !== "production"; - temporary marking this as ignore
  const isTestEnv = true;
  const antIcon = (
    <LoadingOutlined style={{ fontSize: 16, color: "#121212" }} spin />
  );
  const {
    user: {
      loadGetExistingProject,
      projectDelete,
      loadGetCongratulationResults,
      getSingleProjectData,
      getLoadingExistingProject,
      getLoadingDeleteRecord,
      setProjectName,
      loadGetDraftResults,
      loadGetAdvisorResults,
      loadGetTheoryOfChangeResults,
      loadGetNoObjectionProcedureResults,
      getUserType,
      getProjectReportPdf,
    },
  } = useStore(null);

  const userType = getUserType;

  const [openModel, setOpenModel] = useState(false);
  const [projectData, setProjectData] = useState(null);
  const [loadingProjectData, setLoadingProjectData] = useState(true);
  const [downloadLoading, setDownloadLoading] = useState(null);
  const [editLoading, setEditLoading] = useState("");
  const [downloadData, setDownloadData] = useState({
    sections: [],
    sectionDataHandler: null,
  });

  const cleanProjectName = (name: string) => name.replace("`", "'").trim();

  const modifyProjectData = (result: any, type: string, mode?: string) => {
    const data = result?.projects?.[type] || {};

    const projects = Object.keys(data).map((projectName) => ({
      type,
      projectName: cleanProjectName(projectName),
      id: uuidv4(),
      status: data[projectName] || null,
      modelUsed: data[projectName][3],
      completion: data[projectName][0],
      updatedAt: data[projectName][1],
      lastSection: data[projectName][2],
      ...(mode ? { mode } : {}),
    }));

    // lets order the projects by last updated date
    projects.sort((a, b) => {
      const dateA = new Date(a.updatedAt);
      const dateB = new Date(b.updatedAt);
      return dateB.getTime() - dateA.getTime();
    });

    return projects;
  };

  const handleLoadProject = async (payload) => {
    setProjectData(null);
    setLoadingProjectData(true);
    const result = await loadGetExistingProject().catch(() => {
      navigate(constRoute.login);
    });

    if (!result) {
      return;
    }

    // WE ARE DITCHING REFINE A CN FOR NOW
    // const conceptNote = modifyProjectData(result, "concept note")
    //   .map((el) => {
    //     return { ...el, projectName: el.projectName.replace("`", "'").trim() };
    //   })
    //   .filter((el) => el.projectName !== payload?.project_name);

    const cnFull = modifyProjectData(result, "concept note evaluator", "full");

    const cnQuick = modifyProjectData(
      result,
      "concept note assistant",
      "quick",
    );

    const conceptNote = [...cnFull, ...cnQuick];

    // additional check to make sure the order is based on the last updated date. is not necessary for others.
    conceptNote.sort((a, b) => {
      const dateA = new Date(a.updatedAt);
      const dateB = new Date(b.updatedAt);
      return dateB.getTime() - dateA.getTime();
    });

    const advisor = modifyProjectData(result, "project structuring");

    const theoryOfChange = modifyProjectData(result, "theory of change");

    const noObjectionProcedure = modifyProjectData(
      result,
      "no objection procedure",
    );

    const rationalAdvisor = modifyProjectData(result, "climate rationale");

    const cnConverter = modifyProjectData(result, "concept note converter");

    setProjectData({
      "concept note converter": cnConverter,
      "concept note evaluator": conceptNote,
      "concept note assistant": conceptNote,
      "project structuring": advisor,
      "theory of change": theoryOfChange,
      "no objection procedure": noObjectionProcedure,
      "climate rationale": rationalAdvisor,
    });
    setLoadingProjectData(false);
  };

  const deleteProjectData = async () => {
    const payload = {
      project_name: data?.projectName,
      functionality: data?.type,
    };
    const res = await projectDelete(payload);
    if (
      res?.error?.includes("Invalid token") ||
      res?.error?.includes("Token has expired")
    ) {
    } else {
      // lets delete the single project from the projectData in the state
      const updatedProjectData = projectData[data?.type].filter(
        (item) => item.projectName !== data?.projectName,
      );

      setProjectData((prevState) => ({
        ...prevState,
        [data?.type]: updatedProjectData,
      }));

      setOpenModel(false);
    }
  };

  function download(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  const onExportPdf = async (data) => {
    const { projectName, type } = data;
    const name = { project_name: projectName };

    const res = await getProjectReportPdf({
      project_name: projectName,
      email,
      functionality: type?.includes("no objection procedure")
        ? "no objection procedure"
        : type?.includes("theory of change")
          ? "theory of change"
          : type?.includes("evaluator")
            ? "concept note evaluator"
            : type === "concept note"
              ? "concept note"
              : type?.includes("climate rationale")
                ? "climate rationale"
                : "project structuring",
    });

    const url = URL.createObjectURL(res);

    // Create a link element
    const link = document.createElement("a");

    // Set the href and download attributes of the link
    link.href = url;

    link.download = `${projectName}.pdf`;

    // Append the link to the body
    document.body.appendChild(link);

    // Programmatically click the link to download the file
    link.click();

    // Remove the link from the body
    document.body.removeChild(link);
  };

  const handleGenerateReport = async (data) => {
    const payload = {
      project_name: data?.projectName,
      functionality: "concept note",
    };
    await fetch(`${baseUrl}/generateReport`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: getAuthorizationHeader(),
      },
      body: JSON.stringify(payload),
    }).then((response) => {
      response.blob().then((blob) => download(blob, "project"));
    });

    setDownloadLoading(null);
  };

  useEffect(() => {
    setProjectData(null); // Clear cached state
    setTimeout(
      () => handleLoadProject(null), // Refetch data on navigation
      1000,
    );
  }, [pathname]); // Dependency ensures this runs on route change

  const handleGetData = async (item) => {
    setProjectName(item?.projectName);
    console.log(item);
    const payload = {
      project_name: item?.projectName,
      section: item?.type?.includes("no objection procedure")
        ? "PO"
        : item?.type?.includes("evaluator")
          ? "B_1"
          : item?.type?.includes("project structuring")
            ? "pis"
            : item?.type?.includes("theory of change")
              ? "GSR"
              : item?.type?.includes("climate rationale")
                ? "i_24"
                : "B_1",
      functionality: item?.type,
    };
    localStorage.setItem("data-for-tokens", JSON.stringify(payload));
    // !item?.type?.includes("structuring") &&
    //   (await getSingleProjectData(payload).then((res) => {
    //     // setTokens(res["tokens_remaining/tokens_purchased"]);
    //   }));
    setEditLoading("");

    function getNavRoute(projectType, lastEditedSection): string {
      let targetSectionList = [];
      let targetToolBaseUrl: string = "";

      switch (projectType) {
        case "concept note evaluator":
          targetToolBaseUrl = constRoute?.gradeProject;
          targetSectionList = evaluateSectionsMap;
          break;
        case "concept note assistant":
          targetToolBaseUrl = constRoute?.gradeProject + "/quick";
          return targetToolBaseUrl;
        case "project structuring":
          targetToolBaseUrl = constRoute?.advisorProject;
          targetSectionList = renewableProjectSectionsMap;
          break;
        case "concept note":
          targetToolBaseUrl = constRoute?.draftProject;
          targetSectionList = draftSectionsMap;
          break;
        case "climate rationale":
          targetToolBaseUrl = constRoute?.rationalAdvisorProject;
          targetSectionList = climateRationaleSectionsMap;
          break;
        case "concept note converter":
          targetToolBaseUrl = constRoute?.cnConverterProject;
          return targetToolBaseUrl;
        case "theory of change":
          targetToolBaseUrl = constRoute?.theoryOfChangeProject;
          targetSectionList = theoryOfChangeSectionsMap;
          break;
        case "no objection procedure":
          targetToolBaseUrl = constRoute?.noObjectionProcedureProject;
          targetSectionList = noObjectionProcedureSectionsMap;
          break;
      }

      let lastEditedSectionIndex = targetSectionList.findIndex(
        (e) => e.section === lastEditedSection,
      );

      if (lastEditedSectionIndex === -1) {
        lastEditedSectionIndex = 0;
      }

      const formIndex = lastEditedSectionIndex + 1;

      return targetToolBaseUrl + `/form/${formIndex}`;
    }

    localStorage.setItem("projectName", item?.projectName);
    navigate(getNavRoute(item?.type, item.status[2]));
  };

  const noDownloadModes = ["quick"];
  const noDownloadTypes = [
    "concept note converter",
    "climate rationale",
    "theory of change",
  ];

  const actions = (data) => {
    const isDownloadingPDF =
      downloadLoading?.id === data?.id && downloadLoading?.type === "pdf";
    const isDownloadingDocs =
      downloadLoading?.id === data?.id && downloadLoading?.type === "docs";
    const downloadDisabled = downloadLoading === data?.projectName;

    const shouldShowDownload =
      !noDownloadModes.includes(data?.mode) &&
      !noDownloadTypes.includes(data?.type);

    return (
      <div className={style.flexWrapper}>
        {editLoading === data?.projectName ? (
          <Spin indicator={antIcon} />
        ) : (
          <img
            src={pencilIcon}
            className={style.imgClass}
            onClick={() => {
              setEditLoading(data?.projectName);
              handleGetData(data);
              localStorage.removeItem("projectSector");
            }}
          />
        )}

        <img
          src={trashIcon}
          className={style.imgClass}
          onClick={() => {
            setData(data);
            setOpenModel(true);
          }}
        />

        {shouldShowDownload && (
          <>
            {isDownloadingPDF && <Spin indicator={antIcon} />}
            {isDownloadingDocs ? (
              <Spin indicator={antIcon} />
            ) : (
              <img
                style={{
                  pointerEvents: downloadDisabled ? "none" : "auto",
                }}
                src={exportDataIcon}
                className={style.imgClass}
                onClick={() => {
                  onExportPdf(data);
                }}
              />
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <div className={style.homePagePageContainer}>
        <div>
          <div className={style.projectHeading}>Projects</div>
          <p className={style.firstdivaragraph}>
            Find all the project applications you’re overseeing below.
          </p>

          {/* WE ARE DITCHING REFINE A CN FOR NOW */}
          {/*<ProjectsTable*/}
          {/*  data={projectData?.conceptNote || []}*/}
          {/*  title="Refine a Concept Note"*/}
          {/*  actions={(data) => actions(data)}*/}
          {/*  loadingProjectData={loadingProjectData}*/}
          {/*  setEditLoading={(data) => setEditLoading(data)}*/}
          {/*  handleGetData={(data) => handleGetData(data)}*/}
          {/*  fileIconColor="#244A32"*/}
          {/*  userType={userType}*/}
          {/*/>*/}

          {isTestEnv ? (
            <>
              <ProjectsTable
                data={(projectData && projectData["climate rationale"]) || []}
                title="Climate Rationale Advisor"
                actions={(data) => actions(data)}
                loadingProjectData={loadingProjectData}
                setEditLoading={(data) => setEditLoading(data)}
                handleGetData={(data) => handleGetData(data)}
                fileIconColor="#1A9244"
                userType={userType}
              />

              <ProjectsTable
                data={(projectData && projectData["theory of change"]) || []}
                title="Theory of Change"
                actions={(data) => actions(data)}
                loadingProjectData={loadingProjectData}
                setEditLoading={(data) => setEditLoading(data)}
                handleGetData={(data) => handleGetData(data)}
                fileIconColor="#418F5C"
                userType={userType}
              />

              {/* DISABLED NO OBJECTION AND PROJECT STRUCTURING */}
              {/*<ProjectsTable*/}
              {/*  data={(projectData && projectData["project structuring"]) || []}*/}
              {/*  title="Renewable Energy Project Structuring"*/}
              {/*  actions={(data) => actions(data)}*/}
              {/*  loadingProjectData={loadingProjectData}*/}
              {/*  setEditLoading={(data) => setEditLoading(data)}*/}
              {/*  handleGetData={(data) => handleGetData(data)}*/}
              {/*  fileIconColor="#1A9244"*/}
              {/*  userType={userType}*/}
              {/*/>*/}

              {/*<ProjectsTable*/}
              {/*  data={*/}
              {/*    (projectData && projectData["no objection procedure"]) || []*/}
              {/*  }*/}
              {/*  title="No Objection Procedure"*/}
              {/*  actions={(data) => actions(data)}*/}
              {/*  loadingProjectData={loadingProjectData}*/}
              {/*  setEditLoading={(data) => setEditLoading(data)}*/}
              {/*  handleGetData={(data) => handleGetData(data)}*/}
              {/*  fileIconColor="#418F5C"*/}
              {/*  userType={userType}*/}
              {/*/>*/}
            </>
          ) : null}

          <ProjectsTable
            data={(projectData && projectData["concept note assistant"]) || []}
            title="Concept Note Evaluator"
            actions={(data) => actions(data)}
            loadingProjectData={loadingProjectData}
            setEditLoading={(data) => setEditLoading(data)}
            handleGetData={(data) => handleGetData(data)}
            fileIconColor="#00B840"
            userType={userType}
          />

          <ProjectsTable
            data={(projectData && projectData["concept note converter"]) || []}
            title="Concept Note Converter"
            actions={(data) => actions(data)}
            loadingProjectData={loadingProjectData}
            setEditLoading={(data) => setEditLoading(data)}
            handleGetData={(data) => handleGetData(data)}
            fileIconColor="#1A9244"
            userType={userType}
          />
        </div>
        <ProjectDeleteModelData
          loading={getLoadingDeleteRecord}
          isOpen={openModel}
          closeModal={() => setOpenModel(false)}
          onDeleteRecord={deleteProjectData}
        />
      </div>
      <div
        id="exportContent"
        style={{ visibility: "hidden", position: "absolute", zIndex: -1 }}
      >
        <ContentToExport
          sections={downloadData?.sections}
          sectionDataHandler={downloadData?.sectionDataHandler}
        />
      </div>
    </>
  );
};

export default ExistingProject;
